import SearchIcon from '@mui/icons-material/Search'
import { Box, Button, Checkbox, TextField, Typography } from '@mui/material'
import InputAdornment from '@mui/material/InputAdornment'
import Table from 'common/components/Table'
import { CustomModalContext } from 'common/contexts/CustomModalContext'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import useFetchLoV from '../hooks/useFetchLoV'

const SelectorConBuscador = ({
  open,
  onClose = () => {},
  campo,
  codigo_lv,
  expediente,
  multiple = false,
  onChange,
  placeholder = 'Buscar...',
  outSearch,
  value,
  filtros,
}) => {
  const { handleModal } = useContext(CustomModalContext)

  const [busqueda, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selected, setSelected] = useState(multiple ? (value ?? []) : (value ?? null))
  const [options, setOptions] = useState([])
  const [page, setPage] = useState(1)
  const [columns, setColumns] = useState([])
  const [hasMore, setHasMore] = useState(false)
  const keyCounterRef = useRef(0)
  const optionCount = useMemo(() => options?.length || 12, [options])
  const [esCodigoLoV, setEsCodigoLoV] = useState('')
  const [descripcionLoV, setDescripcionLoV] = useState('')
  const autoSelectedRef = useRef(false)

  const { response, loading, error } = useFetchLoV({
    codigo_lv,
    campo,
    expediente,
    page,
    busqueda: debouncedSearch && debouncedSearch.trim() !== '' ? debouncedSearch : outSearch,
    filtros,
  })

  const handleCerrar = () => {
    handleModal({ enabled: false })
  }

  const isSame = (a, b) => String(a ?? '') === String(b ?? '')

  const getComparableValue = (row) => {
    if (!row) return ''
    return esCodigoLoV && row[esCodigoLoV] != null ? row[esCodigoLoV] : row.value
  }

  const handleConfirmar = (nextSelected = selected) => {
    if (multiple) {
      const seleccionados = options.filter(
        (opt) =>
          Array.isArray(nextSelected) &&
          nextSelected.some((v) => isSame(v, getComparableValue(opt))),
      )
      onChange?.(seleccionados)
    } else {
      const seleccionado =
        options.find((opt) => isSame(getComparableValue(opt), nextSelected)) ?? null
      onChange?.(seleccionado)
    }
  }

  const toggleOpcion = (row) => {
    if (!row) return

    const lovVal = esCodigoLoV && row[esCodigoLoV] != null ? row[esCodigoLoV] : row.value

    if (multiple) {
      setSelected((prev) => {
        const exists = prev.some((v) => isSame(v, lovVal))
        const next = exists ? prev.filter((v) => !isSame(v, lovVal)) : [...prev, lovVal]
        return next
      })
    } else {
      setSelected(lovVal)
      handleConfirmar(lovVal)
      handleCerrar()
    }
  }

  useEffect(() => {
    autoSelectedRef.current = false
  }, [open])

  useEffect(() => {
    if (
      open &&
      !multiple &&
      !loading &&
      !autoSelectedRef.current &&
      page === 1 &&
      Number(response?.registros_list?.length) === 1 &&
      options.length === 1
    ) {
      autoSelectedRef.current = true
      toggleOpcion(options[0])
      handleCerrar()
    }
  }, [open, multiple, loading, page, response?.total_registros, options])

  useEffect(() => {
    if (!open) return
    setPage(1)
    setHasMore(false)
    keyCounterRef.current = 0
  }, [open, codigo_lv, campo, expediente])

  const mergeUniqueBy = (prev, next, key) => {
    const seen = new Set(prev.map((it) => it[key]))
    const merged = [...prev]
    for (const item of next) {
      if (!seen.has(item[key])) {
        merged.push(item)
        seen.add(item[key])
      }
    }
    return merged
  }

  useEffect(() => {
    setSearch('')
    setDebouncedSearch('')
    setPage(1)
  }, [open])

  useEffect(() => {
    if (!open || !response) return

    let lovField = ''
    let descField = ''
    const cols = (response.columnas_list ?? []).map((c) => {
      if (c?.es_codigo_lov && c?.es_codigo_lov !== 'false') {
        lovField = String(c?.codigo ?? '').toLowerCase()
        setEsCodigoLoV(lovField)
      }
      if (c?.es_desc_lov && c?.es_desc_lov !== 'false') {
        descField = String(c?.codigo ?? '').toLowerCase()
        setDescripcionLoV(String(c?.codigo ?? '').toLowerCase())
      }
      return {
        field: String(c?.codigo ?? '').toLowerCase(),
        title: c?.etiqueta ?? c?.codigo ?? '',
      }
    })

    if (multiple) {
      cols.unshift({
        field: 'checkbox',
        title: '',
        customGetter: (row) => {
          const lovVal = esCodigoLoV && row[esCodigoLoV] != null ? row[esCodigoLoV] : row.value
          const checked = Array.isArray(selected) && selected.some((v) => isSame(v, lovVal))
          return (
            <Checkbox
              defaultChecked={checked}
              onChange={(e) => {
                toggleOpcion(row)
              }}
            />
          )
        },
      })
    }

    setColumns(cols)

    const rows = (response.registros_list ?? []).map((r) => {
      const row = {}
      for (let i = 0; i < cols.length; i++) {
        const f = cols[i].field
        row[f] = r?.[f]
      }
      const valueField =
        'value' in (r || {})
          ? 'value'
          : 'id' in (r || {})
            ? 'id'
            : cols.find((c) => c.field !== 'checkbox')?.field

      row.value = r?.[valueField]

      if (lovField) {
        row[lovField] = r?.[lovField] ?? null
        row.esCodigoLoV = r?.[lovField] ?? null
      }

      if (descField) {
        row[descField] = r?.[descField] ?? null
        row.descripcionLoV = r?.[descField] ?? null
      }

      row.__key = `${row.value ?? 'null'}-${keyCounterRef.current++}`

      return row
    })

    setOptions((prev) => {
      const newOptions = page > 1 ? mergeUniqueBy(prev, rows, 'value') : rows

      const totalRegistros = Number(response?.total_registros ?? newOptions.length)
      setHasMore(newOptions.length < totalRegistros)

      return newOptions
    })
  }, [response, open, page])

  useEffect(() => {
    if (!open) return
    setSelected(multiple ? (value ?? []) : (value ?? null))
  }, [open, value, multiple])

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(busqueda)
    }, 500)

    return () => {
      clearTimeout(handler)
    }
  }, [busqueda])

  const handleMostrarMas = () => {
    if (!loading && hasMore && page <= Number(response?.numero_paginas)) {
      setPage((p) => p + 1)
    }
  }

  return (
    <Box
      sx={{
        background: 'white',
        p: 4,
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        minWidth: '50vw',
      }}
    >
      <Typography variant="h2">Seleccion de elementos</Typography>
      <TextField
        fullWidth
        variant="outlined"
        placeholder={placeholder}
        value={busqueda}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="end">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
      />
      <Box sx={{ overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        <Table
          fields={columns}
          dataSource={options}
          rowKey="__key"
          onRowClick={!multiple ? (r) => toggleOpcion(r.row) : null}
          loading={loading}
          rowsPerPage={optionCount}
        />
        <Button
          variant="text"
          onClick={handleMostrarMas}
          disabled={!hasMore || loading || page == response?.numero_paginas}
        >
          {loading ? 'Cargando…' : hasMore ? 'Mostrar más' : 'No hay más'}
        </Button>
      </Box>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          onClick={() => {
            handleCerrar()
          }}
          sx={{ flexGrow: 1 }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            handleConfirmar()
            handleCerrar()
          }}
          sx={{ flexGrow: 1 }}
        >
          Aceptar
        </Button>
      </Box>
    </Box>
  )
}

export default SelectorConBuscador
